import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, X, Mic, FileText, Zap } from 'lucide-react';
import {
    trackConversionEvent,
    recordUpgradeTimestamp,
} from '../utils/telemetry';
import { getExperimentVariant } from '../services/api';

/**
 * UpgradeActivationBanner — Shown once after successful plan upgrade.
 *
 * Phase 3B: Helps the user immediately experience premium value.
 * Shows plan-specific next-best-action suggestions.
 *
 * Props:
 *   newPlan         – 'Pro' or 'Premium'
 *   source          – attribution source that led to upgrade
 *   onDismiss       – callback when banner is dismissed
 *   onAction        – callback(actionId) when user clicks an action
 *
 * Actions suggested by plan:
 *   Pro:      generate lesson (enhanced), explore voice styles
 *   Premium:  generate lesson (premium), try premium audio, explore presets
 */

const PLAN_ACTIONS = {
    Pro: [
        {
            id: 'generate_lesson',
            icon: FileText,
            label: 'Generate a Lesson',
            description: 'Now with deeper instructional reasoning',
        },
        {
            id: 'explore_voices',
            icon: Mic,
            label: 'Try Your Voice Styles',
            description: '3 voice presets now unlocked',
        },
    ],
    Premium: [
        {
            id: 'generate_lesson',
            icon: FileText,
            label: 'Generate a Premium Lesson',
            description: 'Full context + selective refinement',
        },
        {
            id: 'explore_voices',
            icon: Mic,
            label: 'Explore Premium Voice Styles',
            description: '4 presets including Calm Directions',
        },
        {
            id: 'generate_audio',
            icon: Zap,
            label: 'Create Full Lesson Audio',
            description: 'Premium narration scripts + longer content',
        },
    ],
};

const UpgradeActivationBanner = ({ newPlan = 'Pro', source = 'direct', onDismiss, onAction }) => {
    const [visible, setVisible] = useState(true);
    const variant = getExperimentVariant('activation_banner_variant', 'A');

    useEffect(() => {
        // Record upgrade timestamp for adoption analytics
        recordUpgradeTimestamp();

        trackConversionEvent('plan_upgrade_completed', {
            new_plan_tier: newPlan,
            source,
            experiment_name: 'activation_banner',
            variant,
        });
        trackConversionEvent('post_upgrade_prompt_shown', {
            new_plan_tier: newPlan,
            source,
            experiment_name: 'activation_banner',
            variant,
        });
    }, [newPlan, source, variant]);

    if (!visible) return null;

    const actions = PLAN_ACTIONS[newPlan] || PLAN_ACTIONS.Pro;
    const planColor = newPlan === 'Premium' ? '#8b5cf6' : '#f59e0b';
    const planGradient = newPlan === 'Premium'
        ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)'
        : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)';
    const planBorder = newPlan === 'Premium' ? '#c4b5fd' : '#fcd34d';

    const handleAction = (actionId) => {
        trackConversionEvent('post_upgrade_prompt_clicked', {
            new_plan_tier: newPlan,
            action: actionId,
            source,
            experiment_name: 'activation_banner',
            variant,
        });
        if (onAction) onAction(actionId);
        handleDismiss();
    };

    const handleDismiss = () => {
        setVisible(false);
        if (onDismiss) onDismiss();
    };

    return (
        <div style={{
            background: planGradient,
            border: `1px solid ${planBorder}`,
            borderRadius: 16,
            padding: '20px 24px',
            marginBottom: 20,
            position: 'relative',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
            {/* Close button */}
            <button
                onClick={handleDismiss}
                style={{
                    position: 'absolute', top: 12, right: 12,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', padding: 4,
                }}
            >
                <X size={16} />
            </button>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: planColor, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                }}>
                    <Sparkles size={16} style={{ color: '#fff' }} />
                </div>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                        Welcome to {newPlan}! 🎉
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Your teaching experience just got better. Here's what to try first:
                    </div>
                </div>
            </div>

            {/* Action cards */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {actions.map(action => {
                    const Icon = action.icon;
                    return (
                        <button
                            key={action.id}
                            onClick={() => handleAction(action.id)}
                            style={{
                                flex: '1 1 200px',
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '12px 14px', borderRadius: 10,
                                border: '1px solid rgba(0,0,0,0.08)',
                                background: '#fff', cursor: 'pointer',
                                textAlign: 'left', transition: 'all 0.15s',
                            }}
                            onMouseOver={e => {
                                e.currentTarget.style.borderColor = planColor;
                                e.currentTarget.style.boxShadow = `0 2px 8px ${planColor}20`;
                            }}
                            onMouseOut={e => {
                                e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: `${planColor}15`, display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                <Icon size={14} style={{ color: planColor }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                    {action.label}
                                </div>
                                <div style={{ fontSize: 11, color: '#6b7280' }}>
                                    {action.description}
                                </div>
                            </div>
                            <ArrowRight size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default UpgradeActivationBanner;
